import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { 
  Building2, Plus, Trash2, Key, Search, ArrowLeft, 
  User, Mail, Globe, CheckCircle, XCircle, Eye, EyeOff
} from 'lucide-react';
import { toast } from 'sonner';
import Header from '../../components/Header';
import api from '../../utils/api';
import { toFullUrl } from '../../utils/imageUrl';

const AdminCompanies = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Dialog states
  const [showAddCompany, setShowAddCompany] = useState(false);
  const [showSetPassword, setShowSetPassword] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  
  // Form states
  const [newCompany, setNewCompany] = useState({
    legal_name: '',
    email: '',
    password: '',
    full_name: '',
    country: '',
    website: '',
    description: ''
  });
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [companiesRes, usersRes] = await Promise.all([
        api.get('/admin/companies'),
        api.get('/admin/users')
      ]);
      setCompanies(companiesRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCompany = async (e) => {
    e.preventDefault();
    
    if (!newCompany.legal_name || !newCompany.email || !newCompany.password || !newCompany.full_name) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (newCompany.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    try {
      await api.post('/admin/companies', newCompany);
      toast.success(`Company "${newCompany.legal_name}" created successfully!`);
      setShowAddCompany(false);
      setNewCompany({ legal_name: '', email: '', password: '', full_name: '', country: '', website: '', description: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create company');
    }
  };

  const handleSetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    try {
      await api.post(`/admin/users/${selectedUser.id}/set-password`, { new_password: newPassword });
      toast.success(`Password updated for ${selectedUser.email}`);
      setShowSetPassword(false);
      setSelectedUser(null);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set password');
    }
  };

  const handleDeleteCompany = async () => {
    try {
      await api.delete(`/admin/companies/${selectedCompany.id}`);
      toast.success(`Company "${selectedCompany.legal_name}" deleted`);
      setShowDeleteConfirm(false);
      setSelectedCompany(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete company');
    }
  };

  const openSetPassword = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setShowSetPassword(true);
  };

  const openDeleteConfirm = (company) => {
    setSelectedCompany(company);
    setShowDeleteConfirm(true);
  };

  const filteredCompanies = companies.filter(c =>
    c.legal_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.primary_user?.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-slate-50 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-slate-50 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-4 mb-6">
            <Button variant="ghost" onClick={() => navigate('/admin')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Admin
            </Button>
          </div>

          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-4xl font-outfit font-bold text-ocean-900">Company Management</h1>
              <p className="text-slate-600 mt-2">Add, manage, and remove companies from the network</p>
            </div>
            <Button
              onClick={() => setShowAddCompany(true)}
              className="bg-cyan-500 hover:bg-cyan-600"
              data-testid="add-company-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Company
            </Button>
          </div>

          {/* Search */}
          <Card className="mb-6">
            <CardContent className="p-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search by company name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="company-search"
                />
              </div>
            </CardContent>
          </Card>

          {/* Companies List */}
          <div className="space-y-4">
            {filteredCompanies.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center text-slate-500">
                  <Building2 className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p>No companies found</p>
                </CardContent>
              </Card>
            ) : (
              filteredCompanies.map((company) => (
                <Card key={company.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-4">
                        {toFullUrl(company.logo_url) ? (
                          <img
                            src={toFullUrl(company.logo_url)}
                            alt={company.legal_name}
                            className="w-16 h-16 rounded-lg object-cover"
                            onError={(e) => e.target.style.display = 'none'}
                          />
                        ) : (
                          <div className="w-16 h-16 bg-slate-100 rounded-lg flex items-center justify-center">
                            <Building2 className="h-8 w-8 text-slate-400" />
                          </div>
                        )}
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-lg">{company.legal_name}</h3>
                            <Badge variant={company.status === 'active' ? 'default' : 'secondary'}>
                              {company.status}
                            </Badge>
                          </div>
                          
                          {company.primary_user && (
                            <div className="flex items-center gap-4 text-sm text-slate-600 mb-2">
                              <span className="flex items-center gap-1">
                                <User className="h-3 w-3" />
                                {company.primary_user.full_name}
                              </span>
                              <span className="flex items-center gap-1">
                                <Mail className="h-3 w-3" />
                                {company.primary_user.email}
                              </span>
                              {company.primary_user.is_verified ? (
                                <span className="flex items-center gap-1 text-green-600">
                                  <CheckCircle className="h-3 w-3" />
                                  Verified
                                </span>
                              ) : (
                                <span className="flex items-center gap-1 text-orange-600">
                                  <XCircle className="h-3 w-3" />
                                  Not Verified
                                </span>
                              )}
                            </div>
                          )}
                          
                          {company.website && (
                            <a
                              href={company.website.startsWith('http') ? company.website : `https://${company.website}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-cyan-600 hover:underline flex items-center gap-1"
                            >
                              <Globe className="h-3 w-3" />
                              {company.website}
                            </a>
                          )}
                          
                          <p className="text-xs text-slate-400 mt-2">
                            Created: {new Date(company.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {company.primary_user && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openSetPassword(company.primary_user)}
                            title="Set Password"
                            data-testid={`set-password-${company.id}`}
                          >
                            <Key className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-500 border-red-200 hover:bg-red-50"
                          onClick={() => openDeleteConfirm(company)}
                          title="Delete Company"
                          data-testid={`delete-company-${company.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          {/* Add Company Dialog */}
          <Dialog open={showAddCompany} onOpenChange={setShowAddCompany}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Add New Company</DialogTitle>
                <DialogDescription>
                  Create a new company with a user account. The company will be automatically approved.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleAddCompany} className="space-y-4 pt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Company Name *</Label>
                    <Input
                      value={newCompany.legal_name}
                      onChange={(e) => setNewCompany(prev => ({ ...prev, legal_name: e.target.value }))}
                      placeholder="Acme Logistics"
                      required
                      data-testid="new-company-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Country</Label>
                    <Input
                      value={newCompany.country}
                      onChange={(e) => setNewCompany(prev => ({ ...prev, country: e.target.value }))}
                      placeholder="Jamaica"
                      data-testid="new-company-country"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Contact Person Name *</Label>
                  <Input
                    value={newCompany.full_name}
                    onChange={(e) => setNewCompany(prev => ({ ...prev, full_name: e.target.value }))}
                    placeholder="John Smith"
                    required
                    data-testid="new-company-contact"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Email Address *</Label>
                  <Input
                    type="email"
                    value={newCompany.email}
                    onChange={(e) => setNewCompany(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="[email protected]"
                    required
                    data-testid="new-company-email"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Password *</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={newCompany.password}
                      onChange={(e) => setNewCompany(prev => ({ ...prev, password: e.target.value }))}
                      placeholder="Min 6 characters"
                      required
                      data-testid="new-company-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Website</Label>
                  <Input
                    value={newCompany.website}
                    onChange={(e) => setNewCompany(prev => ({ ...prev, website: e.target.value }))}
                    placeholder="https://www.example.com"
                    data-testid="new-company-website"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={newCompany.description}
                    onChange={(e) => setNewCompany(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Brief description of the company..."
                    rows={3}
                    data-testid="new-company-description"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowAddCompany(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-cyan-500 hover:bg-cyan-600" data-testid="create-company-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Create Company
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          {/* Set Password Dialog */}
          <Dialog open={showSetPassword} onOpenChange={setShowSetPassword}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Set Password</DialogTitle>
                <DialogDescription>
                  Set a new password for {selectedUser?.email}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label>New Password</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Min 6 characters"
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div className="flex justify-end gap-3">
                  <Button variant="outline" onClick={() => setShowSetPassword(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSetPassword}
                    className="bg-cyan-500 hover:bg-cyan-600"
                    data-testid="save-password-btn"
                  >
                    <Key className="h-4 w-4 mr-2" />
                    Set Password
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Delete Confirmation Dialog */}
          <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="text-red-600">Delete Company</DialogTitle>
                <DialogDescription>
                  Are you sure you want to delete "{selectedCompany?.legal_name}"? This will also delete:
                  <ul className="list-disc ml-6 mt-2 text-sm">
                    <li>All user accounts associated with this company</li>
                    <li>All RFQs created by this company</li>
                    <li>All quotes submitted by this company</li>
                  </ul>
                  <p className="mt-2 font-semibold text-red-600">This action cannot be undone.</p>
                </DialogDescription>
              </DialogHeader>
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleDeleteCompany}
                  className="bg-red-500 hover:bg-red-600"
                  data-testid="confirm-delete-btn"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Company
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </>
  );
};

export default AdminCompanies;
